import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/auth-context';
import { useMandateContext } from '../contexts/mandate-context';
import { supabase, SUPABASE_URL } from '../integrations/supabase/client';
import { logger } from '../utils/logger';

const PayPalCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, organization } = useAuth();
  const { selectedMandateId } = useMandateContext();
  
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    handlePayPalCallback();
  }, []);

  const handlePayPalCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        throw new Error(`PayPal OAuth error: ${error}`);
      }

      if (!code || !state) {
        throw new Error('Missing PayPal authorization code or state');
      }

      if (!selectedMandateId || !organization?.id) {
        throw new Error('Missing mandate or organization context');
      }

      // Parse state to get mandate and organization
      const [mandateId, orgId] = state.split('_');
      
      if (mandateId !== selectedMandateId || orgId !== organization.id) {
        throw new Error('State mismatch - possible security issue');
      }

      // Route the code exchange securely via Edge Function (no client-side secrets)
      const url = new URL(`${SUPABASE_URL}/functions/v1/paypal-sync/callback`);
      url.searchParams.set('code', code);
      url.searchParams.set('state', state);
      const callbackRes = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!callbackRes.ok) {
        const text = await callbackRes.text();
        throw new Error(`Callback failed: ${callbackRes.status} ${text}`);
      }

      // Optionally, fetch PayPal profile info via server later if needed
      const connectionEmail = 'PayPal account';

      // Create real PayPal connection in database
      const realConnection = {
        mandate_id: selectedMandateId,
        organization_id: organization.id,
        paypal_account_email: connectionEmail,
        paypal_account_id: state,
        access_token: 'stored-by-server',
        refresh_token: null,
        token_expires_at: null,
        connection_status: 'active' as const,
        settings: {
          currencyMappings: {
            USD: '1030',
            CHF: '1030',
            EUR: '1031'
          },
          classificationRules: [],
          reconciliationSettings: {
            autoMatch: true,
            matchingTolerance: 0.01,
            dateRangeDays: 3
          },
          mandateSpecific: true,
          oauthState: state
        }
      };

      const { data, error: dbError } = await supabase
        .from('paypal_connections')
        .upsert(realConnection, { onConflict: 'mandate_id' })
        .select()
        .single();

      if (dbError) throw dbError;

      setStatus('success');
      setMessage(`PayPal successfully connected!`);

      // Redirect back to settings after 3 seconds
      setTimeout(() => {
        navigate('/settings?tab=integrations');
      }, 3000);

    } catch (err) {
      logger.error('PayPal callback error:', err);
      setStatus('error');
      setMessage(`Failed to connect PayPal: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">PayPal Connection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'processing' && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mr-3" />
              <span>Processing PayPal connection...</span>
            </div>
          )}

          {status === 'success' && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {status === 'success' && (
            <p className="text-sm text-muted-foreground text-center">
              Redirecting to settings...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayPalCallback;
